import { Card, ColorPicker, Divider, Grid, Text, TextInput } from '@mantine/core';
import { useState } from 'react';
import useUploadAsset from '../../../helpers/useUploadAsset';
import ErrorMessage from '../../ErrorMessage';
import ImageInput from '../../ImageInput';

// The brand token vocabulary is closed: the search layouts read exactly these
// keys, so this is a fixed set of inputs rather than a key/value editor.
const brandTokenFields = [
  { key: 'accent', label: 'Accent', description: 'Falls back to the primary color above when left empty.' },
  { key: 'surface', label: 'Surface' },
  { key: 'surfaceMuted', label: 'Surface (muted)' },
  { key: 'text', label: 'Text' },
  { key: 'textMuted', label: 'Text (muted)' },
  { key: 'border', label: 'Border' },
  { key: 'radius', label: 'Radius' },
  { key: 'shadow', label: 'Shadow' },
  { key: 'fontFamily', label: 'Font family' },
  { key: 'cardAspectRatio', label: 'Card aspect ratio' },
  { key: 'gridColumns', label: 'Grid columns' },
  { key: 'density', label: 'Density' },
];

const SettingsTabVisuals = ({ domain, handleChange }) => {
  const [userError, setUserError] = useState();
  const { uploadReferenceImage } = useUploadAsset(setUserError);

  const handleBrandTokenChange = (key, value) => {
    const brandTokens = { ...domain.brandTokens };
    if (value.trim()) {
      brandTokens[key] = value;
    } else {
      // An empty input means "use the layout default", so drop the key instead
      // of persisting an empty string that would override that default.
      delete brandTokens[key];
    }

    handleChange({ target: { name: 'brandTokens', value: Object.keys(brandTokens).length ? brandTokens : null } });
  };

  return (
    <Card withBorder>
      <Card.Section p="md">
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Logo</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            {domain.logoUrl && <img style={{ paddingBottom: 8 }} height="160" alt="shop logo" src={domain.logoUrl} />}
            <ImageInput
              setFile={(file) => {
                uploadReferenceImage(
                  { title: `Shop logo uploaded at ${new Date().toISOString()}`, description: 'Domain logo' },
                  file,
                  (uploadedAsset) => {
                    handleChange({ target: { name: 'logoUrl', value: uploadedAsset } });
                  }
                );
              }}
            />
            {userError && <ErrorMessage message={userError.message} />}
          </Grid.Col>
        </Grid>
        <Divider my="md" />
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Primary Color</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <ColorPicker
              value={domain.primaryColor || undefined}
              onChange={(value) => handleChange({ target: { name: 'primaryColor', value } })}
            />
          </Grid.Col>
        </Grid>
        <Divider my="md" />
        <Grid mb="md">
          <Grid.Col span={12}>
            <label>Brand Tokens</label>
            <Text size="xs" c="dimmed" mt="xs">
              Design tokens the search layouts read. Leave a token empty to use the layout&apos;s own default.
            </Text>
          </Grid.Col>
          {brandTokenFields.map(({ key, label, description }) => (
            <Grid.Col span={{ base: 12, sm: 6 }} key={key}>
              <TextInput
                label={label}
                description={description}
                autoComplete="nope"
                value={domain.brandTokens?.[key] || ''}
                onChange={(e) => handleBrandTokenChange(key, e.target.value)}
              />
            </Grid.Col>
          ))}
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default SettingsTabVisuals;
